import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import {
  Megaphone, Plus, Check, Loader2, AlertCircle, Trash2, ExternalLink,
  Facebook, Linkedin, Globe, Send, History,
} from "lucide-react";

const PLATFORMS = [
  { id: "meta", name: "Meta (Facebook + Instagram)", icon: Facebook, brandFrom: "from-blue-500", brandTo: "to-purple-600" },
  { id: "google", name: "Google Ads", icon: Globe, brandFrom: "from-amber-400", brandTo: "to-rose-500" },
  { id: "linkedin", name: "LinkedIn Marketing", icon: Linkedin, brandFrom: "from-sky-600", brandTo: "to-indigo-700" },
];

const OBJECTIVES = [
  { id: "LINK_CLICKS", label: "Traffic / Link clicks" },
  { id: "LEAD_GEN", label: "Lead generation" },
  { id: "REACH", label: "Reach" },
  { id: "BRAND_AWARENESS", label: "Brand awareness" },
  { id: "CONVERSIONS", label: "Conversions / Sales" },
];

export default function Ads() {
  const [status, setStatus] = useState(null);
  const [accounts, setAccounts] = useState([]);
  const [pushes, setPushes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showPush, setShowPush] = useState(false);

  const load = async () => {
    const [s, a, p] = await Promise.all([
      api.get("/ads/status"),
      api.get("/ads/accounts"),
      api.get("/ads/pushes"),
    ]);
    setStatus(s.data); setAccounts(a.data); setPushes(p.data);
    setLoading(false);
  };

  useEffect(() => { load(); }, []);

  if (loading) return <div className="h-64 glass rounded-2xl animate-pulse" />;

  return (
    <div className="space-y-8" data-testid="ads-root">
      <header>
        <div className="text-xs uppercase tracking-[0.25em] text-indigo-400 mb-2 flex items-center gap-2">
          <Megaphone className="h-3.5 w-3.5" /> Ad Push · Multi-platform
        </div>
        <h1 className="font-heading text-3xl lg:text-5xl font-black tracking-tight">Push ads to live ad platforms</h1>
        <p className="text-white/50 mt-3 max-w-2xl text-sm">
          Create campaigns in Meta, Google and LinkedIn Ad Managers directly from MakeYourBrand.IN. All campaigns are pushed in <strong>PAUSED / DRAFT</strong> state — you finalise budgets &amp; targeting in the native ads manager.
        </p>
      </header>

      <section className="grid md:grid-cols-3 gap-4">
        {PLATFORMS.map((p) => {
          const st = status[p.id] || {};
          const acc = accounts.find((a) => a.platform === p.id);
          return (
            <PlatformCard key={p.id} platform={p} status={st} account={acc} reload={load} />
          );
        })}
      </section>

      <section className="flex items-center justify-between">
        <div>
          <h3 className="font-heading text-xl font-black">Push a new ad campaign</h3>
          <p className="text-xs text-white/40">Drafts your campaign in the selected platform's ad manager. Nothing goes live without your confirmation.</p>
        </div>
        <button onClick={() => setShowPush(true)} data-testid="ad-push-open"
          className="inline-flex items-center gap-2 px-4 py-2 rounded-lg brand-gradient text-sm font-semibold hover:opacity-90">
          <Send className="h-4 w-4" /> New ad push
        </button>
      </section>

      {showPush && <PushModal onClose={() => setShowPush(false)} onPushed={load} status={status} />}

      <section className="glass rounded-2xl p-6" data-testid="ad-push-history">
        <h3 className="font-heading text-lg font-black mb-4 flex items-center gap-2">
          <History className="h-4 w-4 text-indigo-400" /> Push history · {pushes.length}
        </h3>
        {pushes.length === 0 ? (
          <div className="text-sm text-white/40 text-center py-6">No ad pushes yet.</div>
        ) : (
          <div className="space-y-2">
            {pushes.map((row) => (
              <div key={row.id} className="glass rounded-lg p-3 flex items-center gap-3 text-xs" data-testid={`ad-push-${row.id}`}>
                <span className="font-mono text-white/40 w-44 shrink-0">{new Date(row.created_at).toLocaleString()}</span>
                <span className="text-[10px] uppercase tracking-[0.15em] text-indigo-300 w-16">{row.platform}</span>
                <span className="text-white/70 flex-1 truncate">{row.name}</span>
                <span className="font-mono text-white/50">₹{row.payload?.daily_budget_inr}/d</span>
                <span className="text-emerald-300">{row.result?.status || "OK"}</span>
                {row.result?.review_url && (
                  <a href={row.result.review_url} target="_blank" rel="noreferrer" className="text-indigo-300 hover:underline inline-flex items-center gap-1">
                    <ExternalLink className="h-3 w-3" /> open
                  </a>
                )}
              </div>
            ))}
          </div>
        )}
      </section>
    </div>
  );
}

function PlatformCard({ platform, status, account, reload }) {
  const [editing, setEditing] = useState(false);
  const [adAccountId, setAdAccountId] = useState(account?.ad_account_id || "");
  const [accessToken, setAccessToken] = useState("");
  const [refreshToken, setRefreshToken] = useState("");
  const [saving, setSaving] = useState(false);

  const save = async () => {
    if (!adAccountId.trim()) { toast.error("Ad Account ID required"); return; }
    setSaving(true);
    try {
      const body = { platform: platform.id, ad_account_id: adAccountId.trim() };
      if (accessToken.trim()) body.access_token = accessToken.trim();
      if (refreshToken.trim()) body.refresh_token = refreshToken.trim();
      await api.post("/ads/accounts", body);
      toast.success(`${platform.name} ad account saved`);
      setEditing(false); setAccessToken(""); setRefreshToken("");
      reload();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
    } finally { setSaving(false); }
  };

  const remove = async () => {
    if (!window.confirm(`Unlink ${platform.name} ad account?`)) return;
    await api.delete(`/ads/accounts/${platform.id}`);
    toast.success("Unlinked"); reload();
  };

  const ready = status.ready;
  const Icon = platform.icon;

  return (
    <div className="glass rounded-2xl p-5 tilt-card" data-testid={`ad-platform-${platform.id}`}>
      <div className="flex items-center gap-3 mb-3">
        <div className={`h-10 w-10 rounded-lg bg-gradient-to-br ${platform.brandFrom} ${platform.brandTo} grid place-items-center`}>
          <Icon className="h-5 w-5 text-white" />
        </div>
        <div>
          <div className="font-heading font-black">{platform.name}</div>
          {ready ? (
            <span className="inline-flex items-center gap-1 text-[10px] uppercase tracking-[0.2em] text-emerald-300">
              <Check className="h-3 w-3" /> Ready
            </span>
          ) : (
            <span className="inline-flex items-center gap-1 text-[10px] uppercase tracking-[0.2em] text-amber-300">
              <AlertCircle className="h-3 w-3" /> Setup needed
            </span>
          )}
        </div>
      </div>

      <div className="space-y-1 text-xs text-white/60 mb-3">
        {platform.id === "meta" && (
          <>
            <Line ok={status.oauth_connected} label="Meta OAuth connected" />
            <Line ok={status.ad_account_set} label={`Ad Account ID set ${status.ad_account_id ? `(${status.ad_account_id})` : ""}`} />
          </>
        )}
        {platform.id !== "meta" && (
          <>
            <Line ok={status.admin_configured} label="Admin keys configured" />
            <Line ok={status.user_connected} label="User token saved" />
            <Line ok={status.ad_account_set} label={`Ad Account ID set ${status.ad_account_id ? `(${status.ad_account_id})` : ""}`} />
          </>
        )}
      </div>

      {!editing && (
        <div className="flex gap-2">
          <button onClick={() => setEditing(true)} data-testid={`ad-platform-edit-${platform.id}`}
            className="inline-flex items-center gap-1 px-3 py-1.5 rounded-lg glass text-xs hover:bg-white/5">
            <Plus className="h-3 w-3" /> {account ? "Update" : "Link"}
          </button>
          {account && (
            <button onClick={remove} data-testid={`ad-platform-remove-${platform.id}`}
              className="inline-flex items-center gap-1 px-3 py-1.5 rounded-lg glass text-xs text-rose-300 hover:bg-rose-500/10">
              <Trash2 className="h-3 w-3" /> Unlink
            </button>
          )}
        </div>
      )}

      {editing && (
        <div className="space-y-2 mt-2">
          <input value={adAccountId} onChange={(e) => setAdAccountId(e.target.value)}
            placeholder={platform.id === "meta" ? "act_1234567890" : platform.id === "google" ? "1234567890" : "5012345"}
            data-testid={`ad-account-id-${platform.id}`}
            className="w-full bg-black/40 border border-white/10 rounded-lg px-3 py-2 text-xs font-mono" />
          {platform.id === "linkedin" && (
            <input type="password" value={accessToken} onChange={(e) => setAccessToken(e.target.value)}
              placeholder="LinkedIn Ads access token"
              data-testid={`ad-token-${platform.id}`}
              className="w-full bg-black/40 border border-white/10 rounded-lg px-3 py-2 text-xs font-mono" />
          )}
          {platform.id === "google" && (
            <input type="password" value={refreshToken} onChange={(e) => setRefreshToken(e.target.value)}
              placeholder="OAuth refresh token"
              data-testid={`ad-token-${platform.id}`}
              className="w-full bg-black/40 border border-white/10 rounded-lg px-3 py-2 text-xs font-mono" />
          )}
          <div className="flex gap-2">
            <button onClick={save} disabled={saving} data-testid={`ad-account-save-${platform.id}`}
              className="inline-flex items-center gap-1 px-3 py-1.5 rounded-lg brand-gradient text-xs font-semibold hover:opacity-90 disabled:opacity-50">
              {saving ? <Loader2 className="h-3 w-3 animate-spin" /> : <Check className="h-3 w-3" />} Save
            </button>
            <button onClick={() => setEditing(false)} className="px-3 py-1.5 rounded-lg glass text-xs">Cancel</button>
          </div>
        </div>
      )}
    </div>
  );
}

function Line({ ok, label }) {
  return (
    <div className="inline-flex items-center gap-1.5">
      {ok ? <Check className="h-3 w-3 text-emerald-400 shrink-0" /> : <span className="h-3 w-3 rounded-full bg-white/10 shrink-0" />}
      <span className={ok ? "text-white/70" : "text-white/40"}>{label}</span>
    </div>
  );
}

function PushModal({ onClose, onPushed, status }) {
  const readyPlatforms = ["meta", "google", "linkedin"].filter((p) => status[p]?.ready);
  const [form, setForm] = useState({
    platform: readyPlatforms[0] || "meta", name: "",
    objective: "LINK_CLICKS", daily_budget_inr: 500,
    headline: "", primary_text: "", description: "", landing_url: "", image_url: "",
  });
  const [pushing, setPushing] = useState(false);

  const push = async () => {
    if (!form.name.trim()) { toast.error("Campaign name required"); return; }
    setPushing(true);
    try {
      const { data } = await api.post("/ads/push", form);
      toast.success(`Campaign queued · ${data.status || "OK"}`);
      onPushed(); onClose();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Push failed");
    } finally { setPushing(false); }
  };

  return (
    <div className="fixed inset-0 z-50 grid place-items-center p-4 bg-black/60" onClick={onClose}>
      <div className="glass-strong border border-white/10 rounded-2xl p-6 w-full max-w-2xl"
        onClick={(e) => e.stopPropagation()} data-testid="ad-push-modal">
        <h3 className="font-heading text-xl font-black mb-4">New ad push</h3>
        {readyPlatforms.length === 0 && (
          <div className="glass rounded-lg p-3 mb-4 text-sm text-amber-300 border border-amber-500/30">
            No platforms are ready. Link an ad account first.
          </div>
        )}
        <div className="grid md:grid-cols-2 gap-3">
          <Lab label="Platform">
            <select value={form.platform} onChange={(e) => setForm({ ...form, platform: e.target.value })}
              data-testid="ad-push-platform"
              className="w-full bg-black/40 border border-white/10 rounded-lg px-3 py-2 text-sm font-mono">
              {readyPlatforms.length === 0 && <option value="">— none —</option>}
              {readyPlatforms.map((p) => <option key={p} value={p}>{p}</option>)}
            </select>
          </Lab>
          <Lab label="Objective">
            <select value={form.objective} onChange={(e) => setForm({ ...form, objective: e.target.value })}
              data-testid="ad-push-objective"
              className="w-full bg-black/40 border border-white/10 rounded-lg px-3 py-2 text-sm">
              {OBJECTIVES.map((o) => <option key={o.id} value={o.id}>{o.label}</option>)}
            </select>
          </Lab>
          <Lab label="Campaign name">
            <input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })}
              data-testid="ad-push-name"
              className="w-full bg-black/40 border border-white/10 rounded-lg px-3 py-2 text-sm" />
          </Lab>
          <Lab label="Daily budget (INR)">
            <input type="number" value={form.daily_budget_inr}
              onChange={(e) => setForm({ ...form, daily_budget_inr: parseInt(e.target.value || 0, 10) })}
              data-testid="ad-push-budget" min={100} max={1000000}
              className="w-full bg-black/40 border border-white/10 rounded-lg px-3 py-2 text-sm font-mono" />
          </Lab>
          <Lab label="Landing URL" full>
            <input value={form.landing_url} onChange={(e) => setForm({ ...form, landing_url: e.target.value })}
              placeholder="https://yoursite.com/offer"
              data-testid="ad-push-landing"
              className="w-full bg-black/40 border border-white/10 rounded-lg px-3 py-2 text-sm font-mono" />
          </Lab>
          <Lab label="Headline">
            <input value={form.headline} onChange={(e) => setForm({ ...form, headline: e.target.value })}
              data-testid="ad-push-headline"
              className="w-full bg-black/40 border border-white/10 rounded-lg px-3 py-2 text-sm" />
          </Lab>
          <Lab label="Image URL">
            <input value={form.image_url} onChange={(e) => setForm({ ...form, image_url: e.target.value })}
              data-testid="ad-push-image"
              className="w-full bg-black/40 border border-white/10 rounded-lg px-3 py-2 text-sm font-mono" />
          </Lab>
          <Lab label="Primary text" full>
            <textarea value={form.primary_text} onChange={(e) => setForm({ ...form, primary_text: e.target.value })}
              data-testid="ad-push-primary-text" rows={3}
              className="w-full bg-black/40 border border-white/10 rounded-lg px-3 py-2 text-sm" />
          </Lab>
        </div>
        <div className="flex justify-end gap-2 mt-5">
          <button onClick={onClose} className="px-4 py-2 rounded-lg glass text-sm">Cancel</button>
          <button onClick={push} disabled={pushing || readyPlatforms.length === 0}
            data-testid="ad-push-submit"
            className="inline-flex items-center gap-2 px-4 py-2 rounded-lg brand-gradient text-sm font-semibold hover:opacity-90 disabled:opacity-50">
            {pushing ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />} Push to ad manager
          </button>
        </div>
      </div>
    </div>
  );
}

function Lab({ label, children, full }) {
  return (
    <label className={`block ${full ? "md:col-span-2" : ""}`}>
      <span className="text-xs text-white/40 uppercase tracking-[0.2em]">{label}</span>
      <div className="mt-1">{children}</div>
    </label>
  );
}
